// Results (case-study index) and case-study stories, from sales &
// marketing's site direction (docs/claude-artifact-examples/
// xeratec-site-preview.html, "Results" and "Stories"). Photos are original
// asset filenames from the captured case-study pages (see lib/links.js).

/** Results page filters. Ids match the home page's "See more" links. */
export const resultTrades = [
  { id: "dirt", label: "Dirt & Excavation" },
  { id: "trucking", label: "Trucking & Hauling" },
  { id: "mechanical", label: "Mechanical & GC" },
  { id: "electrical", label: "Electrical & Utility" },
  { id: "mfg", label: "Equipment & Manufacturing" },
];

export const tradeLabel = (id) =>
  resultTrades.find((trade) => trade.id === id)?.label;

export const resultsPage = {
  eyebrow: "Real companies · Real numbers",
  title: "Companies like yours. *Headaches like yours.* Gone.",
  lede: "Every job below started the same way: a business drowning in spreadsheets, double entry, and disconnected tools. Here's what got built, and what changed — at a glance.",
  image: "R5__8363.jpg",
  imageAlt: "A Burns Dirt dump truck on a haul road",
  filterLabel: "Show me:",
  moreEyebrow: "More from the field",
  moreTitle: "More projects. *Same playbook.*",
  moreText:
    "Process first, then the tech. A few more companies that handed us a headache — read the full write-ups.",
  cta: {
    title: "Your company could be the next card on this page.",
    text: "You've seen the headaches and the numbers. Book sixty minutes and find out what your card would say.",
  },
};

export const storyCta = {
  title: "Your story could be *next.*",
  text: "Every one of these started with a single conversation about what was eating somebody's week. Bring yours — we'll tell you straight what it would take to fix it.",
};

/** Industry shown for a study: its own `industry`, else its filter's label. */
export const industryLabel = (study) =>
  study.industry || tradeLabel(study.trade);

export const ljjCaseStudy = "/ljj-construction-from-the-jobsite-to-quickbooks/";

/**
 * The headline case studies, keyed by page path. `photo` heads the story
 * page, `photos` sit in its body, `screenshot` (or several `screenshots`;
 * `phone: true` for a phone-shaped one) shows the product, and `logo` is
 * the client's mark. `stats` and `quote` show when there are any.
 */
export const caseStudyDetails = {
  "/norco-transport-driving-efficiency-across-every-load/": {
    trade: "trucking",
    company: "Norco Transport",
    who: "Hauling & logistics · Greeley, Colorado · Oil & gas, construction, environmental",
    build: "EPIC Logistics",
    headaches: [
      "Thousands of loads a month tracked across countless Excel sheets",
      "Every driver ticket touched three times — log it, invoice it, pay the vendor — three chances for errors",
      "Rates varied by distance, weight, service, even day of the week; formulas kept breaking",
      "Hand-built invoices delayed billing 2–3 days",
    ],
    modules: [
      "Smart Load Import",
      "Dynamic Rate Engine",
      "One-Click Invoicing",
      "Contractor Settlements",
      "QuickBooks Online Sync",
      "Live Dashboards",
    ],
    runningNow: [
      "Tickets entered once — the system flags errors automatically",
      "Loads that took hours to enter process in minutes",
      "Office staff focus on growth, not spreadsheet troubleshooting",
    ],
    stats: [
      { value: "80%", label: "less manual data entry" },
      { value: "60%", label: "less office time on billing" },
      { value: "Same-day", label: "invoicing (was 2–3 days)" },
      { value: "3×", label: "the load volume, same team" },
      { value: "10,000+", label: "loads handled per month" },
    ],
    quote: {
      text: "Previously, each ticket was handled three separate times — creating three different opportunities for errors. With our new software, the system flags errors automatically, making our process much more reliable.",
      name: "Eddie Leos",
      role: "CEO, Norco Transport",
    },
    story: {
      title: "Norco Transport: *driving efficiency across every load*",
      who: "Hauling & logistics · Greeley, Colorado · Serving oil & gas, construction, and environmental customers",
      situation: [
        "Norco Transport moves thousands of loads every month — and for years, every one of them lived in Excel. Spreadsheets multiplied across the office, and every driver ticket got handled three separate times: once to log it, once to invoice it, and once to pay the vendor. Three touches meant three chances for an error on every single load.",
        "The rates made it worse. Pricing varied by distance, weight, service type — even the day of the week — and the spreadsheet formulas trying to keep up kept breaking. Invoices were built by hand, which delayed billing two to three days on work that was already done.",
      ],
      builtHeading: "What we built: EPIC Logistics",
      built:
        "EPIC Logistics puts the whole flow in one place. Load data imports and maps automatically instead of being re-keyed. A dynamic rate engine handles the pricing variables that used to break formulas. Invoices generate with a click, contractor settlements come from the same data, and everything syncs to QuickBooks Online — with dashboards showing the state of the business in real time.",
      runningNow:
        "Tickets are entered once. The system flags errors automatically instead of letting them ride through to the invoice. Billing that used to lag days behind the work now goes out the same day — and the office team spends its time growing the business instead of troubleshooting spreadsheets.",
    },
    photo: "20230503_093241_original.webp",
    photoAlt: "A Norco Transport truck and trailer on a dirt job site",
    photos: [
      {
        src: "123_1_3_original.webp",
        alt: "A Norco Transport tanker truck at sunrise",
      },
      {
        src: "Norco_treater_haul_pic_original.webp",
        alt: "A Norco Transport truck hauling a treater on a flatbed",
      },
    ],
    screenshot: {
      src: "dispatch-log-scaled.png",
      alt: "The EPIC Logistics dispatch log, listing each day's loads by customer",
      caption: "The EPIC Logistics dispatch log: every load, entered once.",
    },
    logo: "brand/clients/Norco-Transport.png",
  },

  "/burns-dirt-bidding-dirt-moving-smarter/": {
    trade: "dirt",
    company: "Burns Dirt Construction",
    who: "Heavy civil earthwork & hauling · Mississippi & Alabama",
    build: "BattleShip",
    headaches: [
      "A “Moneyball” bidding strategy trapped in a Google Sheets prototype",
      "The spreadsheet kept breaking — powerful, but fragile and impossible to share",
      "Haul costs modeled on assumptions instead of live fuel and mileage data",
    ],
    modules: [
      "Haul Scenario Modeling",
      "Pit & Fleet Comparison",
      "Cost-per-Ton Engine",
      "Live Fuel & Mapping Data",
      "Competitor Bid Simulation",
    ],
    runningNow: [
      "Anyone on the team can run real bid scenarios — not just the spreadsheet guy",
      "Bids modeled on live numbers, not gut feel",
      "The platform is built to scale to other dirt contractors",
    ],
    stats: [
      {
        value: "Faster",
        label: "bid turnaround — no manual spreadsheet modeling",
      },
      {
        value: "Sheets → App",
        label: "fragile prototype became a reliable platform",
      },
      {
        value: "Live data",
        label: "real-time fuel & mapping in every cost model",
      },
      {
        value: "Whole team",
        label: "adoption through a clean, simple interface",
      },
    ],
    quote: {
      text: "We had a solid prototype in Sheets — but it kept breaking, and we hit a wall. Xeratec turned our rough concept into a clean, reliable tool anyone on our team can use. BattleShip took our ‘Moneyball’ strategy from idea to execution — it's now baked into how we operate.",
      name: "Nic Parish",
      role: "CEO, Burns Dirt Construction",
    },
    story: {
      title: "Burns Dirt: *bidding dirt moving smarter*",
      who: "Heavy civil earthwork & hauling · Mississippi & Alabama",
      situation: [
        "Burns Dirt had a genuinely smart idea: a “Moneyball” approach to bidding — modeling haul scenarios with data instead of gut feel. The problem wasn't the strategy. It was the container. The whole thing lived in a Google Sheets prototype that was powerful but fragile: it kept breaking, it couldn't scale, and it was nearly impossible to share beyond the person who built it.",
        "Worse, the cost modeling behind it ran on assumptions — estimated fuel prices, estimated mileage — when the real numbers were out there to be had.",
      ],
      builtHeading: "What we built: BattleShip",
      built:
        "BattleShip turned the prototype into a real platform. It models haul scenarios across pits and fleets, calculates true cost-per-ton, pulls live fuel and mapping data into every estimate, and can even simulate competitor bids — all behind a clean interface anyone on the team can use, not just the spreadsheet guy.",
      runningNow:
        "Bidding strategy isn't an idea in a fragile spreadsheet anymore — it's baked into how Burns Dirt operates every day. Bids turn around faster, run on live numbers, and the platform is built to scale to other dirt contractors down the road.",
    },
    photo: "1752854103933.jpeg",
    photoAlt: "A Burns Dirt crew member waving from the cab of a truck",
    photos: [
      { src: "R6__0165.jpg", alt: "A Burns Dirt excavator on a cleared site" },
      {
        src: "Burns-260.jpg",
        alt: "A Burns Dirt surveyor in a safety vest on a job site",
      },
    ],
    logo: "burns_dirt_construction_inc_logo-150x150.jpeg",
  },

  "/jrt-mechanical-streamlining-operations-with-custom-project-management-software/":
    {
      trade: "mechanical",
      company: "JRT Mechanical",
      who: "Full-service mechanical contractor · HVAC, plumbing & electrical · Pacific Northwest",
      build: "One Project Platform",
      headaches: [
        "$140,000+ a year spent on 10+ software tools",
        "Only 20–40% of the features ever got used",
        "Tools didn't talk to each other — departments worked in silos",
      ],
      modules: [
        "RFIs",
        "Change Orders",
        "Daily Reports",
        "Time Tracking",
        "Task Management",
        "Meeting Tool",
        "Recurring Tasks",
      ],
      runningNow: [
        "One integrated, process-driven platform across every department",
        "Tasks link to any module and hand off automatically between teams",
        "Doing more work with less overhead",
      ],
      stats: [
        { value: "$140k+", label: "annual software spend consolidated" },
        {
          value: "10+ → 1",
          label: "disconnected tools replaced by one platform",
        },
        {
          value: "100%",
          label: "purpose-built — no paying for unused features",
        },
      ],
      quote: {
        text: "This new platform has transformed the way we manage our projects. We're now able to do more with less, and the efficiency gains have been incredible. The fact that everything is integrated and process-driven has made a huge difference in our day-to-day operations.",
        name: "Carl Tapani",
        role: "President, JRT Mechanical Inc.",
      },
      story: {
        title: "JRT Mechanical: *one platform instead of ten*",
        who: "Full-service mechanical contractor · HVAC, plumbing & electrical · Pacific Northwest",
        situation: [
          "JRT Mechanical was spending more than $140,000 a year on software — spread across more than ten separate tools. Each one was bought to solve a real problem, but the pile had become the problem: teams used maybe 20–40% of any tool's features, none of the tools talked to each other, and every department worked in its own silo.",
        ],
        builtHeading: "What we built: one integrated project platform",
        built:
          "Instead of ten tools that almost fit, we built one that actually does. RFIs, change orders, daily reports, time tracking, and task management live in a single process-driven platform. Tasks link to any module and hand off automatically between teams, so work flows across departments instead of dying at the boundaries between apps.",
        runningNow:
          "Every department works in the same system, following the same process. The subscription pile — and the six-figure annual bill behind it — is gone, replaced by a platform where 100% of what JRT pays for is something they actually use.",
      },
      photo: "Artboard-2.jpg",
      photoAlt: "The JRT Mechanical team in front of a company banner",
      photos: [
        {
          src: "58241401-4d51-4457-a511-e3339642110b.webp",
          alt: "A mechanical room with boilers and piping",
        },
      ],
      screenshot: {
        src: "tasks-in-pipline_new-task.jpg",
        alt: "The JRT project platform's task pipeline with a new task form open",
        caption:
          "Tasks link to any module and hand off between teams automatically.",
      },
      logo: "JRT-logo-e1723169483734.png",
    },

  // Written for the new site: no captured page, so content/written.js
  // (casePages) creates one. Photos are from LJJ's website; the
  // screenshots are a demo copy of the app with fictional data.
  [ljjCaseStudy]: {
    trade: "mechanical",
    industry: "General Contracting",
    company: "LJJ Construction",
    who: "Commercial & industrial general contractor · Steel, concrete, excavating & utilities · Houghton, Michigan",
    build: "Custom field-to-office platform",
    headaches: [
      "Crew hours on paper timecards, sorted by hand into phase codes and pay types",
      "Every hour typed into QuickBooks a second time for payroll",
      "Daily reports, RFIs, change orders and vendor bills spread across paper and email",
      "Job costs only as current as the last time someone caught up",
    ],
    modules: [
      "Offline Crew Time App",
      "Time Allocation & Approval",
      "Daily Reports & Site Photos",
      "Digital Safety Meetings",
      "RFIs, Submittals & Change Orders",
      "Progress Invoicing & Retainage",
      "Payables & Job Ledger",
      "PTO & Time Off",
      "QuickBooks Online Sync",
      "AI Report Builder",
    ],
    runningNow: [
      "Crew hours entered once in the field land in QuickBooks without being retyped",
      "Project managers see a job's reports, photos, costs and billing in one place",
      "Accounting works from approved numbers instead of chasing them",
    ],
    story: {
      title: "LJJ Construction: *from the jobsite to QuickBooks, entered once*",
      who: "Commercial & industrial general contractor · Steel, concrete, excavating & utilities · Houghton, Michigan",
      situation: [
        "Since 2008, LJJ Construction has built and renovated commercial, industrial and public buildings around Houghton, Michigan, with work ranging from structural steel to underground utilities. With crews on several jobsites at once, every hour they worked took a long road to a paycheck. Time came in on paper timecards. Someone sorted it by hand into phase codes and pay types, then typed it all into QuickBooks again for payroll.",
        "The rest of the business was just as scattered. Daily reports, site photos, RFIs, change orders, vendor bills and progress billing were spread across paper receipts and email inboxes. The office had become the integration layer. Every handoff, from field to project manager to accounting to QuickBooks, meant someone re-keying data, and job costs were only as current as the last time someone caught up.",
      ],
      builtHeading: "What we built: a custom field-to-office platform",
      built: [
        "The LJJ app connects the jobsite to the books. Crews clock in on a phone app that keeps working without signal and syncs when it reconnects. Those hours are split across phase codes and pay types, approved by the project manager and pushed into QuickBooks Online timesheets without being retyped.",
        "Everything else about a job lives in the same place: daily reports, site photos, safety meetings, RFIs, submittals and change orders. Vendor bills post to QuickBooks automatically, and progress invoices with retainage feed a live job ledger. When someone needs a number the screens don't show, they ask the built-in AI report builder in plain English.",
      ],
      runningNow:
        "Hours are entered once, on the jobsite, and move through approval to payroll without anyone retyping them. Project managers see a job's daily reports, photos, costs and billing in one place. Accounting works from approved numbers instead of chasing them, and QuickBooks stays in step with the field.",
    },
    photo: "case-studies/ljj-barge-excavator.jpg",
    photoAlt: "An LJJ excavator and dump truck working from a barge in fog",
    photos: [
      {
        src: "case-studies/ljj-precast-panels.jpg",
        alt: "An LJJ crew setting precast concrete wall panels with a crane",
      },
      {
        src: "case-studies/ljj-welcome-center.jpg",
        alt: "A finished welcome center building built by LJJ",
      },
    ],
    screenshots: [
      {
        src: "case-studies/ljj-time-allocation.png",
        alt: "The Time Allocation screen, with crew hours split by phase code and pay type",
        caption:
          "Crew hours from the field, allocated and approved in one place.",
      },
      {
        src: "case-studies/ljj-timekeeper-app.png",
        alt: "The Timekeeper phone app, clocked in on a job",
        caption: "Crews clock in from the jobsite, even with no signal.",
        phone: true,
      },
    ],
    logo: "case-studies/ljj-logo.png",
    stats: [],
  },

  "/mj-electric-transforming-saftey-with-digital-assessment-solutions/": {
    trade: "electrical",
    company: "MJ Electric",
    who: "Electrical construction · Quanta Services subsidiary · Transmission, wind, substations across North America",
    build: "Digital Safety Platform",
    headaches: [
      "Safety assessments done manually and inconsistently across job sites",
      "Potential compliance gaps between wind farms, substations, and utility work",
      "Teams spent their time gathering data instead of acting on it",
    ],
    modules: [
      "Custom Assessment Builder",
      "Standardized Digital Checklists",
      "Real-Time Safety Dashboards",
      "Trend Reporting & Analytics",
      "Multi-Site Support",
    ],
    runningNow: [
      "Every site runs assessments to one consistent standard",
      "Hazards get addressed from live trend data — before they escalate",
      "Crews spend time on corrective action, not paperwork",
    ],
    stats: [
      {
        value: "Every site",
        label: "one safety standard across all project types",
      },
      { value: "Real-time", label: "hazard trends and compliance insights" },
      { value: "Years", label: "of ongoing partnership and rapid support" },
    ],
    quote: {
      text: "We partnered with Xeratec years ago to deliver technical development to improve our overall business functions. Xeratec has responded rapidly when needed to maintain and develop our technical solutions and keep our systems relevant to the changing needs of our business.",
      name: "Cliff Cook",
      role: "Director of I.T., MJ Electric",
    },
    story: {
      title: "MJ Electric: *transforming safety with digital assessments*",
      who: "Electrical construction · Quanta Services subsidiary · Transmission, wind farms & substations across North America",
      situation: [
        "MJ Electric runs safety-critical work across wildly different environments — wind farms, substations, utility jobs — and their safety assessments were being done manually, with formats and rigor that varied site to site. That inconsistency created potential compliance gaps, and the teams responsible for safety were spending their energy gathering data instead of acting on it.",
      ],
      builtHeading: "What we built: a digital safety platform",
      built:
        "We built MJ Electric a platform that standardizes safety across every kind of site they run. An assessment builder lets them create the exact checklists each project type needs; digital checklists keep every crew to one standard; and real-time dashboards with trend reporting turn thousands of assessments into something leadership can actually act on.",
      runningNow:
        "Every site runs assessments to one consistent standard. Hazards surface in live trend data and get addressed before they escalate. And the partnership continues — years on, we still maintain and evolve the systems as the business changes.",
    },
    photo: "safty-right-img.jpg",
    photoAlt:
      "An MJ Electric lineman setting safety cones beside a utility truck",
    photos: [
      {
        src: "mj-people.jpg",
        alt: "Two MJ Electric workers in hard hats reviewing a part",
      },
    ],
    screenshot: {
      src: "Omega-Intro-Agenda-4.png",
      alt: "The MJ Electric safety assessment list on a laptop",
      caption: "Every assessment, every site, one standard.",
    },
    logo: "Web-4-300x300.png",
    // The mark sits small inside a wide transparent margin.
    logoPadded: true,
  },

  "/somero-enterprises-inc-advancing-precision-with-a-3d-profiler-system/": {
    trade: "mfg",
    company: "Somero Enterprises",
    who: "Concrete equipment manufacturer · Systems used on jobs for Costco, Walmart, Home Depot, FedEx & Tesla",
    build: "3-D Profiler Modernization",
    headaches: [
      "A flagship product running on dated legacy software",
      "Performance limits forced big projects to be chopped into smaller jobs",
      "A hardware company stuck maintaining aging code",
    ],
    modules: [
      "Modernized 3-D Profiler System",
      "Upgraded OpenGL Rendering",
      "Large-Project Data Handling",
      "Ongoing Product Partnership",
    ],
    runningNow: [
      "Massive projects run whole — no more segmenting data into small jobs",
      "Faster processing, fewer errors, more productive job sites",
      "Operators of any skill level can run it and trust the result",
    ],
    stats: [
      { value: "1200%", label: "increase in system performance" },
      {
        value: "Fortune 500",
        label: "floors installed with the system nationwide",
      },
      { value: "Years", label: "of cost-effective, on-time delivery" },
    ],
    quote: {
      text: "Projects typically come with enough headaches — anything that can make my life easier is more than welcomed. This system is a dream come true. I can put it in the hands of any one of my operators and trust that the job will turn out exactly as planned. We'd never work any other way.",
      name: "James Coy",
      role: "President, James K. Coy Construction — a Somero customer",
    },
    story: {
      title: "Somero Enterprises: *modernizing a flagship product*",
      who: "Concrete equipment manufacturer · Systems used on jobs for Costco, Walmart, Home Depot, FedEx & Tesla",
      situation: [
        "Somero builds world-class concrete equipment — but their flagship 3-D Profiler system was running on dated legacy software. Performance limits meant big projects had to be chopped into smaller segments just to process, and a hardware company found itself stuck maintaining aging code that wasn't its core business.",
      ],
      builtHeading: "What we built: the modernized 3-D Profiler",
      built:
        "We became Somero's software department. The modernized 3-D Profiler runs on upgraded OpenGL rendering with data handling built for massive projects — a 1200% performance increase over the legacy system. And the relationship didn't end at delivery: we've supported and evolved the product for years, delivering quality work on time and on budget.",
      runningNow:
        "Massive projects run whole — no more segmenting data into small jobs. Operators of any skill level can run the system and trust the result, on floors installed for some of the largest organizations in the world.",
    },
    photo: "S15R-06-scaled-1.jpg",
    photoAlt: "A Somero laser screed placing a concrete floor in a warehouse",
    photos: [
      {
        src: "uneven-concrete-floor-1024x512-1.webp",
        alt: "A worker checking a concrete floor's level with a measuring rod",
      },
    ],
    logo: "Somero-logo-e1716334105100-qrz29t74v00c8eu4bwxy1l9629gjtlg6t7kulvovmc.png",
  },
};

/**
 * The other captured case studies, shown as photo cards under the five
 * headline stories (and filtered with them).
 */
export const moreProjects = {
  "/michels-corporation-driving-growth-with-strategic-guidance-and-process-optimization/":
    {
      trade: "mechanical",
      company: "Michels Corporation",
      title: "Strategic guidance and process optimization",
      summary:
        "Hundreds of processes reviewed and standardized across departments to reach ISO compliance ahead of international expansion.",
      photo: "person-welding-pipe-2400x1350-1-2048x1152-1-1536x864.webp",
    },
  "/mj-electric-optimizing-operations-with-a-digital-equipment-picklist/": {
    trade: "electrical",
    company: "MJ Electric",
    title: "A digital equipment pick-list",
    summary:
      "Building equipment and tool lists for utility jobs went from manual and error-prone to a custom app — in half the time.",
    photo: "mjstuff.jpg",
  },
  "/mj-electric-streamlining-operations-through-business-process-integration/":
    {
      trade: "electrical",
      company: "MJ Electric",
      title: "Business process integration",
      summary:
        "One web platform pulling from JD Edwards for T&M billing, safety audits, and accruals & forecasting — tablet-friendly for the field.",
      photo: "mj-electric.jpg",
    },
  "/loadtraxx-revolutionizing-loading-and-hauling-with-custom-mobile-app-development/":
    {
      trade: "trucking",
      company: "LoadTraxx",
      title: "A trucker-proof mobile app",
      summary:
        "Paper load tickets replaced by an app veteran drivers actually use — speeding up billing and payroll for dump-truck operations.",
      photo: "loadtraxx2.jpg",
    },
  "/oem-enhancing-efficiency-with-the-lean-manufacturing-assistant/": {
    trade: "mfg",
    company: "OEM",
    title: "The Lean Manufacturing Assistant",
    summary:
      "Real-time tracking of product flow and machine states, so plant teams spot bottlenecks and changeovers before they cost a shift.",
    photo: "Screenshot-2022-12-14-at-9.49.12-AM.png",
    screenshot: true,
  },
};

/** Canonical case-study path for paginated duplicates like /slug/2/. */
export const canonicalCasePath = (path) => path.replace(/\d+\/$/, "");

export const caseDetailFor = (path) =>
  caseStudyDetails[canonicalCasePath(path)];

/** Trade id for any case study (headline or more), for "Back to Results". */
export const caseTradeFor = (path) => {
  const key = canonicalCasePath(path);
  return (caseStudyDetails[key] || moreProjects[key])?.trade;
};

/** Measured outcomes, by path (used by the generic article layouts). */
export const caseResults = Object.fromEntries(
  Object.entries(caseStudyDetails).map(([path, { stats }]) => [
    path,
    stats.filter(({ value }) => /\d/.test(value)),
  ]),
);

export const norcoCaseStudy =
  "/norco-transport-driving-efficiency-across-every-load/";

/** Results for a path, including paginated duplicates like /slug/2/. */
export const resultsFor = (path) => {
  const results = caseResults[canonicalCasePath(path)];
  return results?.length ? results : undefined;
};
